import { Gift, ArrowRight, Sparkles, Zap, TrendingUp } from 'lucide-react';

interface GapliPromoBannerProps {
  onNavigate: () => void;
}

export default function GapliPromoBanner({ onNavigate }: GapliPromoBannerProps) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <button
        onClick={onNavigate}
        className="group w-full relative overflow-hidden rounded-2xl border border-purple-500/30 hover:border-purple-400/50 transition-all duration-500 hover:shadow-[0_0_60px_rgba(168,85,247,0.15)] cursor-pointer text-left"
      >
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0533] via-[#150a30] to-[#0d0620]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,rgba(168,85,247,0.15),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_50%,rgba(236,72,153,0.10),transparent_70%)]" />

        {/* Animated shimmer */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
        </div>

        {/* Decorative orbs */}
        <div className="absolute -top-16 -right-16 w-48 h-48 bg-purple-600/20 rounded-full blur-[60px] pointer-events-none group-hover:bg-purple-500/25 transition-colors duration-500" />
        <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-fuchsia-600/15 rounded-full blur-[50px] pointer-events-none group-hover:bg-fuchsia-500/20 transition-colors duration-500" />

        {/* Content */}
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 p-5 sm:p-6">
          {/* Icon */}
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-purple-500 via-fuchsia-500 to-pink-500 p-[2px] shadow-lg shadow-purple-500/30 group-hover:shadow-purple-500/50 transition-shadow duration-500 shrink-0">
            <div className="w-full h-full bg-[#150a30] rounded-2xl flex items-center justify-center group-hover:bg-[#1a0d35] transition-colors duration-300">
              <Gift className="w-7 h-7 sm:w-8 sm:h-8 text-purple-400 group-hover:text-purple-300 transition-colors group-hover:scale-110 transform duration-300" />
            </div>
          </div>

          {/* Text */}
          <div className="flex-1 text-center sm:text-left min-w-0">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
              <h3 className="text-xl sm:text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-fuchsia-200 to-pink-200">
                Promuj Gapli
              </h3>
              <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-[10px] font-bold uppercase tracking-wider">
                Nowe
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-lg">
              Opublikuj profesjonalną kampanię reklamową Gapli na Facebooku jednym kliknięciem.
              Podaj link partnerski, ustaw budżet — reszta jest automatyczna!
            </p>
          </div>

          {/* Feature badges */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            {[
              { icon: Zap, text: 'Gotowy szablon' },
              { icon: TrendingUp, text: 'Gotowa kreacja' },
              { icon: Sparkles, text: 'Meta Ads' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold whitespace-nowrap">
                <f.icon className="w-3.5 h-3.5" /> {f.text}
              </div>
            ))}
          </div>

          {/* CTA Arrow */}
          <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold text-sm shadow-lg shadow-purple-500/20 group-hover:shadow-purple-500/40 group-hover:from-purple-500 group-hover:to-fuchsia-500 transition-all duration-300 shrink-0 whitespace-nowrap">
            Zacznij teraz
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </div>
        </div>

        {/* Bottom gradient line */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </button>
    </div>
  );
}
